import { useState } from "react";
import { Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebase/config";

import PrimaryButton from "../../components/Buttons/PrimaryButton";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);

      setSuccessMessage(
        "If an account exists with this email, you will receive password reset instructions shortly. Please check your inbox and spam folder."
      );
      setEmail("");
    } catch (error: any) {
      console.error("Password reset error:", error);

      if (error.code === "auth/user-not-found") {
        setSuccessMessage(
          "If an account exists with this email, you will receive password reset instructions shortly. Please check your inbox and spam folder."
        );
        setEmail("");
      } else if (error.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError("Failed to send password reset email. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gray-50">
      <div className="w-full max-w-md px-4">
        <div className="text-left">
          <h2 className="text-3xl font-semibold mb-4 text-left">
            Reset your password
          </h2>
          <p className="text-gray-600 mb-8">
            Enter your email address and we'll send you instructions to reset
            your password.
          </p>

          <div className="max-w-md mx-auto">
            <form onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                  placeholder="Enter your email address"
                />
              </div>

              <PrimaryButton
                className="mt-6 w-full"
                type="submit"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send reset instructions"}
              </PrimaryButton>

              {successMessage && (
                <div
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 mt-4"
                  role="alert"
                >
                  {successMessage}
                </div>
              )}

              {error && (
                <div
                  className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mt-4"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <div className="mt-6 text-center">
                <Link to="/login" className="text-[#1d365d] hover:underline">
                  Back to login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
